'use client';

import { Identity } from '@/containers/Identities/types';
import { useAddOptOut } from '@/containers/OptOutManager/api';
import { formatAreaCode, validateAreaCode } from '@/containers/OptOutManager/utils';
import { IdentitySelector } from '@/shared/components/IdentitySelector/IdentitySelector';
import useToast from '@/containers/ToastMessage/hooks/useToast';
import { useCallback, useState } from 'react';

interface AreaCodeTabProps {
  accountId: string;
  identities: Identity[];
  showIdentitySelector: boolean;
  onSuccess?: () => void;
  onClose: () => void;
}

export const AreaCodeTab: React.FC<AreaCodeTabProps> = ({
  accountId,
  identities,
  showIdentitySelector,
  onSuccess,
  onClose,
}) => {
  const { showSuccess, showError } = useToast();
  const addOptOutMutation = useAddOptOut();

  const [selectedIdentityId, setSelectedIdentityId] = useState<string>(
    identities.length === 1 ? identities[0].identity_id : ''
  );
  const [areaCode, setAreaCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [errors, setErrors] = useState<{
    areaCode?: string;
    identity?: string;
  }>({});

  const validateForm = useCallback(() => {
    const newErrors: typeof errors = {};

    if (!areaCode.trim()) {
      newErrors.areaCode = 'Area code is required';
    } else if (!validateAreaCode(areaCode)) {
      newErrors.areaCode = 'Please enter a valid 3-digit area code';
    }

    if (showIdentitySelector && !selectedIdentityId) {
      newErrors.identity = 'Please select an identity';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [areaCode, selectedIdentityId, showIdentitySelector]);

  const handleAreaCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;

    // Remove any non-digits
    value = value.replace(/\D/g, '');

    // Limit to 3 digits
    if (value.length > 3) {
      value = value.substring(0, 3);
    }

    setAreaCode(value);

    if (errors.areaCode) {
      setErrors((prev) => ({ ...prev, areaCode: undefined }));
    }
  };

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!validateForm()) {
        return;
      }

      setIsSubmitting(true);

      try {
        // Clean area code - remove all non-digits
        const cleanedAreaCode = areaCode.replace(/\D/g, '');

        await addOptOutMutation.mutateAsync({
          account_id: accountId,
          identity_id: selectedIdentityId || identities[0]?.identity_id || accountId,
          dnc_type: 'AREA-CODE',
          value: cleanedAreaCode,
          reason: `Area code opt-out: ${formatAreaCode(cleanedAreaCode)}`,
        });

        showSuccess(
          `Successfully added area code opt-out for ${formatAreaCode(cleanedAreaCode)}`
        );
        onSuccess?.();
        onClose();
      } catch (error) {
        console.error('Failed to add area code opt-out:', error);
        showError('Failed to add area code opt-out. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      validateForm,
      areaCode,
      accountId,
      selectedIdentityId,
      identities,
      addOptOutMutation,
      showSuccess,
      showError,
      onSuccess,
      onClose,
    ]
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Identity Selection - Only show if multiple identities */}
      {showIdentitySelector && (
        <div className="form-control">
          <label className="label">
            <span className="label-text font-medium">Identity</span>
          </label>
          <IdentitySelector
            identities={identities}
            value={selectedIdentityId}
            onChange={(e) => {
              setSelectedIdentityId(e.target.value);
              if (errors.identity) {
                setErrors((prev) => ({ ...prev, identity: undefined }));
              }
            }}
            currentAccountId={accountId}
            className={errors.identity ? 'select-error' : ''}
            placeholder="Select an identity"
          />
          {errors.identity && (
            <label className="label">
              <span className="text-error label-text-alt">{errors.identity}</span>
            </label>
          )}
        </div>
      )}

      {/* Area Code Input */}
      <div className="form-control">
        <label className="label">
          <span className="label-text font-medium">Area Code</span>
        </label>
        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 flex items-center pl-3 text-gray-500">
            (
          </div>
          <input
            type="text"
            className={`input input-bordered w-[75px] px-6 text-sm ${
              errors.areaCode ? 'input-error' : ''
            }`}
            placeholder="555"
            value={areaCode}
            onChange={handleAreaCodeChange}
            maxLength={3}
          />
          <div className="pointer-events-none absolute inset-y-0 left-[58px] z-10 flex items-center pr-3 text-gray-500">
            )
          </div>
        </div>
        {errors.areaCode && (
          <label className="label">
            <span className="text-error label-text-alt">{errors.areaCode}</span>
          </label>
        )}
        <label className="label">
          <span className="label-text-alt text-gray-500">
            Enter a 3-digit US area code (e.g., 555 for (555))
          </span>
        </label>
      </div>

      {/* Info Box */}
      <div className="rounded-lg bg-blue-50 p-4 dark:bg-blue-900/20">
        <div className="flex items-start gap-3">
          <div className="flex h-5 w-5 items-center justify-center rounded-full text-blue-600 dark:text-blue-400">
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="text-sm text-blue-800 dark:text-blue-200">
            <div className="font-medium">Area Code Opt-Out</div>
            <div>
              This will opt-out all phone numbers with the specified area code. No
              additional reason is required as the reason will be automatically set to
              include the area code.
            </div>
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end pt-4">
        <button
          type="submit"
          className={`btn btn-primary ${isSubmitting ? 'loading' : ''}`}
          disabled={isSubmitting}>
          {isSubmitting ? 'Adding...' : 'Add Area Code Opt-Out'}
        </button>
      </div>
    </form>
  );
};
